import { t } from '@lingui/core/macro';
import { ExtractRequestBody } from '@luminovo/api-contracts';
import { getToken } from '@luminovo/auth';
import { isPresent } from '@luminovo/commons';
import { httpQueryKey, useDebugErrorHandler, useHttpQuery, useSuspenseHttpQuery } from '@luminovo/data-stack';
import { http } from '@luminovo/http-client';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import { useHttpMutation } from '../mutation/useHttpMutation';

export function useUser(userId: string = '') {
    return useHttpQuery(
        'GET /users/:userId',
        { pathParams: { userId } },
        { enabled: Boolean(userId), select: (res) => res.data },
    );
}

/**
 * @returns all users from the organization
 */
export function useAllOrganizationUsers() {
    return useSuspenseHttpQuery('GET /users/organization', {}, { select: (response) => response.data });
}

/**
 * @returns all users of the customer
 */
export function useAllCustomerUsers(customerId: string | undefined) {
    return useHttpQuery(
        'GET /users/customers/:customerId',
        { pathParams: { customerId: customerId ?? '' } },
        { select: (response) => response.data, enabled: customerId !== undefined },
    );
}

/**
 * @returns all the contributors of an RfQ
 */
export const useContributors = (rfqId: string, queryOptions?: { enabled?: boolean }) => {
    return useHttpQuery(
        'GET /contributors/rfqs/:rfqId',
        { pathParams: { rfqId } },
        {
            ...queryOptions,
        },
    );
};

/**
 * Helper function to check if the userId passed to it belongs to an admin user
 * @param userId
 * @returns boolean
 */
export function useIsUserAnAdmin(userId?: string) {
    const { data } = useHttpQuery(
        'POST /users/roles/bulk',
        {
            requestBody: { ids: isPresent(userId) ? [userId] : [] },
        },
        {
            select: (res) => res.data[0],
            meta: { globalErrorHandler: false },
            // An invite has no roles to ask for.
            enabled: isPresent(userId),
        },
    );

    return data?.roles?.includes('ems:admin') || data?.roles?.includes('customer:admin');
}

export function useMutationInviteExternalContributorUsers(rfqId: string) {
    const { mutateAsync, checkPermission } = useHttpMutation('POST /contributors/rfqs/:rfqId/invite', {
        snackbarMessage: t`External contributor invited successfully`,
    });

    return {
        mutateAsync: async (requestBody: ExtractRequestBody<'POST /contributors/rfqs/:rfqId/invite'>) => {
            return await mutateAsync({ pathParams: { rfqId }, requestBody });
        },
        checkPermission,
    };
}

export function useMutationDeleteUserInvitation(inviteId: string) {
    const debugErrorHandler = useDebugErrorHandler();
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: () => http('DELETE /users/invites/:inviteId', { pathParams: { inviteId: inviteId } }, getToken()),
        onError: debugErrorHandler,
        onSuccess: async () => {
            await queryClient.invalidateQueries({ queryKey: httpQueryKey('GET /users/customers/:customerId/invites') });
            await queryClient.invalidateQueries({ queryKey: httpQueryKey('GET /users/organization/invites') });
        },
    });
}
